import { FlexColumn } from "@/components/Flex";
import HtmlText from "@/components/HtmlText";
import Mountable from "@/components/styled/div/Mountable";
import { themeColors } from "@/utils/themeColors";
import { useRef } from "react";
import { useChatEditorCommunicator } from "../hooks/useChatEditorCommunicator";
import { EditorContextProvider } from "@/components/FullPageEditor/hooks/useEditorContextSelector";
import { getHtmlFromContent } from "@knowt/editor/helpers/getHtmlFromContent";
import { USER_ATTACHMENTS_BUCKET } from "@knowt/syncing/hooks/media/constants";
import ChatInput from "./ChatInput";

const AIGenerationChatInput = ({
    id,
    placeholder,
    disabled,
    disabledReason,
}: {
    id?: string;
    placeholder: string;
    disabled?: boolean;
    disabledReason?: string;
}) => {
    const inputContainerRef = useRef<HTMLDivElement>(null);
    const chatInputRef = useChatEditorCommunicator(s => s.chatInputRef);
    const inputQuestion = useChatEditorCommunicator(s => s.inputQuestion);
    const isStreaming = useChatEditorCommunicator(s => s.isStreaming);
    const isWaitingForResponse = useChatEditorCommunicator(s => s.isWaitingForResponse);
    const selectedContent = useChatEditorCommunicator(s => s.selectedContent);
    const prompt = useChatEditorCommunicator(s => s.prompt);
    const localEditorAttachments = useChatEditorCommunicator(s => s.localEditorAttachments);
    const chosenSuggestion = useChatEditorCommunicator(s => s.chosenSuggestion);

    const handleUnselect = useChatEditorCommunicator(s => s.handleUnselect);
    const stagePrompt = useChatEditorCommunicator(s => s.stagePrompt);
    const onSend = useChatEditorCommunicator(s => s.processChat);
    const setLocalEditorAttachments = useChatEditorCommunicator(s => s.setLocalEditorAttachments);

    const preparedSelectedContent = selectedContent?.trim();
    const hasSelectedContent = !!preparedSelectedContent;

    const handleSubmit = async () => {
        if (isStreaming || !inputQuestion.length || isWaitingForResponse) return;

        await onSend({
            inputQuestion,
            prompt,
            quote: selectedContent,
        });

        stagePrompt({
            inputQuestion: "",
            prompt: "",
        });
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Backspace" && !inputQuestion.length) {
            handleUnselect();
            stagePrompt({ inputQuestion: "", prompt: "" });
        }
    };

    const handleChange = (content: string | null) => {
        if (!content) {
            stagePrompt({ inputQuestion: "", prompt: "" });
            return;
        }

        stagePrompt({ inputQuestion: content });
    };

    return (
        <EditorContextProvider
            content={getHtmlFromContent({ content: inputQuestion, type: "note" })}
            editable
            allowAutoSave={false}
            disabledCustomPlaceholder
            placeholder={placeholder}>
            <FlexColumn
                ref={inputContainerRef}
                style={{
                    padding: "1rem",
                    backgroundColor: themeColors.background,
                    borderRadius: "2rem",
                    transition: "border-radius 0.2s ease-in-out",
                    margin: "0 2.5rem",
                    overflow: "hidden",
                    flexShrink: 0,
                }}>
                <Mountable mount={hasSelectedContent}>
                    <span
                        style={{
                            borderRadius: "1.4rem",
                            backgroundColor: themeColors.neutralWhite,
                            marginBottom: "1rem",
                            padding: "2rem",
                            overflow: "hidden",
                        }}>
                        <HtmlText
                            className="bodySecondary1"
                            text={preparedSelectedContent}
                            style={{
                                display: "-webkit-box",
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                            }}
                        />
                    </span>
                </Mountable>
                <ChatInput
                    id={id}
                    placeholder={placeholder}
                    inputContainerRef={inputContainerRef}
                    disabled={disabled || isStreaming || !inputQuestion || isWaitingForResponse}
                    disabledReason={disabledReason}
                    onSubmit={handleSubmit}
                    uploadDestination={USER_ATTACHMENTS_BUCKET}
                    onKeyDown={handleKeyDown}
                    chosenSuggestion={chosenSuggestion}
                    onChange={handleChange}
                    ref={chatInputRef}
                    attachments={localEditorAttachments || []}
                    onUpdateAttachments={setLocalEditorAttachments}
                />
            </FlexColumn>
        </EditorContextProvider>
    );
};

export default AIGenerationChatInput;
